<template>
    <div class="tab-pane no-overflow active">
        <div v-if="!incorrectAA" class="wireless-speech-wrapper" @scroll="handleScroll">
            <div v-if="!isEditMode" class="filter-wrapper no-print">
                <div class="multiselect-wrapper filter-miltiselect">
                    <Multiselect
                        v-model="selectedLocations"
                        :options="locationOptions"
                        :placeholder="$t('wirelessSpeech.selectLocations')"
                        :multiple="true"
                        :select-label="emptyLabel"
                        :deselect-label="emptyLabel"
                        :selected-label="emptyLabel"
                        :close-on-select="false"
                        :clean-on-close="true"
                        label="loc_name"
                        track-by="loc_name"
                    >
                        <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
                    </Multiselect>
                </div>
                <div class="filter-select">
                    <SelectBox
                        v-model="filterBy"
                        :options="filterByOptions"
                        :placeholder="$t('wirelessSpeech.filterBy')"
                        class="no-print"
                    />
                </div>
            </div>
            <div v-if="isEditMode" class="edit-filter-wrapper no-print">
                <h4>{{ $t('wirelessSpeech.configureSettings') }}</h4>
                <div class="wireless-speech-filter">
                    <div>{{ $t('wirelessSpeech.selectedLocations') }}</div>
                    <div>{{ getLocationNames }}</div>
                </div>
                <div class="wireless-speech-filter">
                    <div>{{ $t('wirelessSpeech.filterBy') }}</div>
                    <div>{{ getFilterName }}</div>
                </div>
                <table class="common-table wireless-speech-table">
                    <thead>
                        <tr class="tbl-header">
                            <th>
                                {{ $t('wirelessSpeech.table.location') }} ({{ filteredLocations.length }})
                            </th>
                            <th>{{ $t('wirelessSpeech.table.phoneNumber') }}</th>
                            <th>{{ $t('wirelessSpeech.table.comment') }}</th>
                        </tr>
                    </thead>
                </table>
            </div>
            <div v-if="isEditModeAllowed" class="table-wrapper wireless-speech no-print">
                <table class="common-table wireless-speech-table">
                    <tbody
                        v-for="(row, index) in filteredLocations"
                        :key="index"
                        :class="{ 'edit-mode': isEditMode }"
                    >
                        <tr v-if="isEditMode">
                            <td>{{ row.loc_name }}</td>
                            <td>
                                <div class="wrapper">
                                    <span
                                        v-if="$v.filteredLocations.$each[index].phone_number.$invalid"
                                        :id="'tooltiptext-phone-' + index"
                                        class="tooltiptext"
                                        >{{
                                            errorDisplay($v.filteredLocations.$each[index].phone_number, {
                                                val: MAX_PHONE_LENGTH,
                                            })
                                        }}</span
                                    >
                                    <div
                                        :class="[
                                            'input-with-insidelabel',
                                            {
                                                tooltiperror:
                                                    $v.filteredLocations.$each[index].phone_number.$invalid,
                                            },
                                        ]"
                                    >
                                        <input
                                            v-model="row.phone_number"
                                            :placeholder="$t('wirelessSpeech.placeholder.enterNumber')"
                                            type="text"
                                        />
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="wrapper">
                                    <span
                                        v-if="$v.filteredLocations.$each[index].comment.$invalid"
                                        :id="'tooltiptext-comment-' + index"
                                        class="tooltiptext"
                                        >{{
                                            errorDisplay($v.filteredLocations.$each[index].comment, {
                                                val: MAX_COMMENT_LENGTH,
                                            })
                                        }}</span
                                    >
                                    <div
                                        :class="[
                                            'textarea-with-insidelabel',
                                            {
                                                disabled: !row.phone_number,
                                                tooltiperror:
                                                    $v.filteredLocations.$each[index].comment.$invalid,
                                            },
                                        ]"
                                    >
                                        <textarea
                                            :id="'comment-' + row.location_id"
                                            v-model="row.comment"
                                            :placeholder="$t('wirelessSpeech.placeholder.enterComment')"
                                            :disabled="!row.phone_number"
                                            :maxlength="MAX_COMMENT_LENGTH"
                                            @input="handleInput"
                                        ></textarea>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr v-else>
                            <td>{{ row.loc_name }}</td>
                            <td>{{ row.phone_number }}</td>
                            <td>{{ row.comment }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="data-empty no-print">
                <h3 v-text="$t('wirelessSpeech.noLocationsFound')"></h3>
            </div>
            <div class="print-only">
                <div class="filter-table-wrapper">
                    <table class="wireless-speech-filter-table">
                        <tr>
                            <td>{{ $t('wirelessSpeech.selectedLocations') }}</td>
                            <td>{{ getLocationNames }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('wirelessSpeech.filterBy') }}</td>
                            <td>{{ getFilterName }}</td>
                        </tr>
                    </table>
                </div>
                <table v-if="isEditModeAllowed" class="table print-wireless-speech">
                    <thead>
                        <tr>
                            <th>{{ $t('wirelessSpeech.table.location') }}</th>
                            <th>{{ $t('wirelessSpeech.table.phoneNumber') }}</th>
                            <th class="comments">{{ $t('wirelessSpeech.table.comment') }}</th>
                        </tr>
                    </thead>
                    <tbody v-for="(row, index) in filteredLocations" :key="index">
                        <tr>
                            <td>{{ row.loc_name }}</td>
                            <td>{{ row.phone_number_copy }}</td>
                            <td>{{ row.comment_copy }}</td>
                        </tr>
                    </tbody>
                </table>
                <h3 v-else class="data-empty" v-text="$t('wirelessSpeech.noLocationsFound')"></h3>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { resourceExternalPhones } from '../api/resources';
import { getCancelToken } from '../api';
import { sortLocations, ifIsNotCanceled } from '../components/common/helpers';
import mappingState from './common/mappingState';
import Multiselect from 'moxa-vue-multiselect';
import SelectBox from './common/SelectBox';
import { mapState, mapMutations } from 'vuex';
import intersectionBy from 'lodash-es/intersectionBy';
import map from 'lodash-es/map';
import { MAX_PHONE_LENGTH, MAX_COMMENT_LENGTH, SUCCESS, ERROR, ROUTE_NAMES, USER_TYPES } from '../constants';
import { maximumPhoneLength, maximumCommentLength, forbidden, errorDisplay } from '../validators';
import numeric from 'vuelidate/lib/validators/numeric';

const ALL = 1;
const WITH_PHONE = 2;
const WITHOUT_PHONE = 3;
const MIN_COMMENT_TEXTAREA_HEIGHT = 20;

export default {
    components: {
        Multiselect,
        SelectBox,
    },

    mixins: [mappingState],

    constants: {
        MAX_PHONE_LENGTH,
        MAX_COMMENT_LENGTH,
    },

    data() {
        return {
            externalPhones: [],
            selectedLocations: [],
            locationOptions: [],
            filterBy: ALL,
            filterByOptions: [
                { value: ALL, name: this.$t('wirelessSpeech.filterBy.allLocations') },
                { value: WITH_PHONE, name: this.$t('wirelessSpeech.filterBy.locationsWithPhone') },
                { value: WITHOUT_PHONE, name: this.$t('wirelessSpeech.filterBy.locationsWithoutPhone') },
            ],
            filterByValues: [
                { value: ALL, name: this.$t('wirelessSpeech.filterBy.all') },
                { value: WITH_PHONE, name: this.$t('wirelessSpeech.filterBy.withPhone') },
                { value: WITHOUT_PHONE, name: this.$t('wirelessSpeech.filterBy.withoutPhone') },
            ],
            isEditMode: false,
            cancelToken: getCancelToken(),
            emptyLabel: '',
            incorrectAA: false,
        };
    },

    validations() {
        return {
            filteredLocations: {
                $each: {
                    phone_number: { numeric, maximumPhoneLength },
                    comment: { forbidden, maximumCommentLength },
                },
            },
        };
    },

    computed: {
        ...mapState(['unsavedChanges']),

        filteredLocations() {
            let foundLocations = intersectionBy(
                this.externalPhones,
                this.selectedLocations.length > 0 ? this.selectedLocations : this.externalPhones,
                'location_id'
            );
            if (this.filterBy === WITH_PHONE) {
                return foundLocations.filter(item => !!item.phone_number_copy);
            } else if (this.filterBy === WITHOUT_PHONE) {
                return foundLocations.filter(item => !item.phone_number_copy);
            }
            return foundLocations;
        },

        isEditModeAllowed() {
            return this.filteredLocations.length > 0;
        },

        getLocationNames() {
            let namesArray = map(this.selectedLocations, 'loc_name');
            if (namesArray.length > 0) {
                return namesArray.join(', ');
            }
            return this.$t('wirelessSpeech.filterBy.allLocations');
        },

        getFilterName() {
            return this.filterByValues.find(item => item.value === this.filterBy).name;
        },
    },

    watch: {
        filteredLocations: {
            handler() {
                if (this.isEditMode) {
                    this.setUnsavedChanges(true);
                }
            },
            deep: true,
        },

        isEditMode(val) {
            if (val) {
                this.$nextTick(() => {
                    this.autosizeAllTextArea();
                    window.addEventListener('resize', this.autosizeAllTextArea);
                });
            } else {
                window.removeEventListener('resize', this.autosizeAllTextArea);
            }
        },
        externalPhones() {
            window.dispatchEvent(new Event('resize'));
        },
    },

    created() {
        this.showLoading(true);
        this.setLoaded(false);
        this.setEditButton();
        this.updateData(() => {
            this.cancel();
            this.selectedLocations = [];
            this.setEditButton();
            return this.loadData();
        });
        return this.loadData();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.autosizeAllTextArea);
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA', setLoaded: 'SET_LOADED' }),

        errorDisplay,

        /**
         * Configures Edit button for view form
         */
        setEditButton() {
            this.configButton({
                buttonName: 'editButton',
                text: 'common.edit',
                action: () => {
                    this.edit();
                },
                disabled: () => !this.isEditModeAllowed,
            });
        },

        loadData() {
            return this.loadExternalPhones()
                .then(() => {
                    this.setLoaded(true);
                    this.showLoading(false);
                    if (this.userType === USER_TYPES.SUPER_USER && this.incorrectAA) {
                        this.popup({
                            type: ERROR,
                            header: this.$t('common.error'),
                            text: this.$t('error.common.forbidden'),
                        });
                    }
                })
                .catch(ifIsNotCanceled({ callbackAlways: () => this.setLoaded(true) }));
        },

        /**
         * Configures Undo, Save, Cancel buttons for edit form
         */
        setConfigButtons() {
            this.configButton({
                buttonName: 'undoButton',
                text: 'common.undo',
                action: () => {
                    this.undo();
                },
                disabled: () => !this.unsavedChanges,
            });

            this.configButton({
                buttonName: 'saveButton',
                text: 'common.save',
                action: () => {
                    this.saveExternalPhones();
                },
                disabled: () => !(this.unsavedChanges && !this.$v.filteredLocations.$invalid),
            });

            this.configButton({
                buttonName: 'cancelButton',
                text: 'common.cancel',
                action: () => {
                    this.cancel();
                },
            });
        },

        /**
         * Loads all rooms with configured phones and comments
         * @returns {Promise}
         */
        loadExternalPhones() {
            this.externalPhones = [];
            this.locationOptions = [];
            this.incorrectAA = false;
            return resourceExternalPhones({
                cancelToken: this.cancelToken.token,
            }).then(response => {
                let extPhone = response.data ? sortLocations(response.data) : [];
                if (!!extPhone && extPhone[0].assignmentarea_id !== this.userAreaId) {
                    this.incorrectAA = true;
                } else {
                    this.externalPhones = this.setCopyPhoneConfig(response.data);
                    this.locationOptions = this.setLocationOptions(this.externalPhones);
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                    });
                }
            });
        },

        /**
         * Saves configuration for rooms with phones and comments, changes page mode and updates copied data
         * @returns {Promise}
         */
        saveExternalPhones() {
            this.showLoading(true);
            let changes = this.externalPhones
                .filter(
                    item => item.phone_number !== item.phone_number_copy || item.comment !== item.comment_copy
                )
                .map((item, i, externalPhones) => {
                    let phoneNumber = item.phone_number ? item.phone_number.trim() : null;
                    let comment = item.comment ? item.comment.trim() : '';

                    // Clears comment for saving and rendered table
                    if (!phoneNumber && !!comment) {
                        comment = '';
                        externalPhones[i].comment = '';
                    }

                    return {
                        location_id: item.location_id,
                        phone_number: phoneNumber,
                        comment: comment,
                    };
                });

            return resourceExternalPhones({ method: 'post', data: changes })
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.WIRELESS_SPEECH) {
                        this.externalPhones = this.setCopyPhoneConfig(this.externalPhones);
                        this.isEditMode = false;
                        this.removeButtons(['undoButton', 'saveButton', 'cancelButton']);
                        this.setEditButton();
                        this.$nextTick(() => {
                            this.setUnsavedChanges(false);
                        });
                        this.showLoading(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                    }
                })
                .catch(response => {
                    this.showLoading(false);
                    if (response.data && response.data.error) {
                        this.popup({
                            type: ERROR,
                            header: this.$t('common.error'),
                            text: this.$t(response.data.error),
                        });
                    }
                    if (response.status === 403) {
                        // Here if "Wireless Speech" is disabled in NISM GUI (during saving the page data)

                        // Clear page
                        this.externalPhones = [];
                        this.cancel();
                    }
                });
        },

        /**
         * Sets location options for multi-select
         * @param {Array} data - array of locations with wireless speech configuration
         * returns {Array} data - array of objects with location_id and loc_name
         */
        setLocationOptions(data) {
            return data.map(record => ({
                location_id: record.location_id,
                loc_name: record.loc_name,
            }));
        },

        /**
         * Sets edit mode with appropriate buttons for Wireless Speech page, removes Edit button
         */
        edit() {
            this.isEditMode = true;
            this.removeButtons('editButton');
            this.setConfigButtons();
            this.setUnsavedChanges(false);
        },

        /**
         * Reverts changes
         */
        undo() {
            this.externalPhones = this.resetChanges(this.externalPhones);
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        /**
         * Discards all changes and sets view mode with Edit button, removes Undo, Save and Cancel buttons
         */
        cancel() {
            this.isEditMode = false;
            this.removeButtons(['undoButton', 'saveButton', 'cancelButton']);
            this.setEditButton();
            this.externalPhones = this.resetChanges(this.externalPhones);
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        /**
         * Sets initial values for phone numbers and comments
         * @param {Array} data - list of rooms with configuration for wireless speech
         * @returns {Array} initial configuration for wireless speech
         */
        resetChanges(data) {
            return data.map(item => ({
                ...item,
                phone_number: item.phone_number_copy,
                comment: item.comment_copy,
            }));
        },

        /**
         * Updates copied data for phone numbers and comments
         * @param {Array} data - list of rooms with configuration for wireless speech
         * @returns {Array} updated configuration for wireless speech
         */
        setCopyPhoneConfig(data) {
            return data
                ? data.map(item => ({
                      ...item,
                      phone_number_copy: item.phone_number,
                      comment_copy: item.comment,
                  }))
                : [];
        },

        /**
         * Updates height of comment text area automatically
         * @param textarea
         */
        autosizeTextArea(textarea) {
            textarea.style.height = MIN_COMMENT_TEXTAREA_HEIGHT + 'px';
            textarea.style.height = textarea.scrollHeight + 'px';
        },

        /**
         * Updates height of all comment text areas automatically
         */
        autosizeAllTextArea() {
            this.filteredLocations.forEach(item => {
                let textArea = document.getElementById('comment-' + item.location_id);
                this.autosizeTextArea(textArea);
            });
        },

        /**
         * Moves tooltips down accordingly to scrolling
         * @param event
         */
        handleScroll(event) {
            if (this.isEditMode) {
                let editModElements = document.getElementsByClassName('edit-mode');
                Array.prototype.forEach.call(editModElements, (el, idx) => {
                    let tooltiptextPhone = document.getElementById('tooltiptext-phone-' + idx);
                    let tooltiptextComment = document.getElementById('tooltiptext-comment-' + idx);
                    if (tooltiptextPhone) {
                        tooltiptextPhone.style.cssText =
                            el.getBoundingClientRect().y < 325
                                ? `bottom: calc(100% + 7px - ${event.target.scrollTop - el.offsetTop}px)`
                                : 'bottom: calc(100% + 7px)';
                    }
                    if (tooltiptextComment) {
                        tooltiptextComment.style.cssText =
                            el.getBoundingClientRect().y < 325
                                ? `bottom: calc(100% + 7px - ${event.target.scrollTop - el.offsetTop}px)`
                                : 'bottom: calc(100% + 7px)';
                    }
                });
            }
        },

        /**
         * Replaces \n with space in comments while inputting; updates text area height
         * @param event
         */
        handleInput(event) {
            let id = +event.target.id.replace('comment-', '');
            let rowIdx = this.filteredLocations.findIndex(row => row.location_id === id);
            this.filteredLocations[rowIdx].comment = this.filteredLocations[rowIdx].comment.replace(
                /\n/g,
                ' '
            );
            this.$nextTick(() => {
                this.autosizeTextArea(event.target);
            });
        },
    },
};
</script>
